import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { isLocalOrDevEnv } from '@/utils/env.ts';

export const DEV_MODE_FEATURES = [
  'DEEPSIGN_RESET_WIZARD',
  'DEEPSIGN_SIGN8',
  'DEEPA_SUMMARIZE'
] as const;
export type DevModeFeatures = (typeof DEV_MODE_FEATURES)[number];

export const useSettingsDevStore = defineStore(
  'settingsDev',
  () => {
    const devModeEnabled = ref(false);
    const devShowSettings = ref(false);
    const devModeFeatures = ref<DevModeFeatures[]>([]);

    // Be sure that DevMode only ever works on `local` or `dev` envs. The
    // persisted value (if any) is re-applied by the persisted state plugin
    // right after this runs, so an explicit user choice is still respected.
    if (isLocalOrDevEnv()) {
      devModeFeatures.value = [];
      devShowSettings.value = false;
      devModeEnabled.value = true;
    } else {
      devShowSettings.value = false;
      devModeEnabled.value = false;
    }

    const isDevModeAllowed = computed(() => isLocalOrDevEnv());

    function canUseDevFeatures() {
      return isDevModeAllowed.value && devModeEnabled.value;
    }

    function hasDevFeature(feature: DevModeFeatures) {
      return devModeFeatures.value.includes(feature);
    }

    function canUseDevFeature(feature: DevModeFeatures) {
      return canUseDevFeatures() && hasDevFeature(feature);
    }

    return {
      isDevModeAllowed,
      devModeEnabled,
      devShowSettings,
      devModeFeatures,
      canUseDevFeatures,
      hasDevFeature,
      canUseDevFeature
    };
  },
  { persist: true }
);
